import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArchiveRestore } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Row = ({ c, on, toggle, solo }) => (
  <div data-testid={`restore-company-${c.id}`} className={`flex flex-wrap items-start gap-3 rounded-md border p-3 text-sm ${on ? "border-blue-400 bg-blue-50/50" : "border-slate-200"}`}>
    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
    <Checkbox data-testid={`restore-check-${c.id}`} checked={on} onCheckedChange={toggle} className="mt-0.5 h-5 w-5 border-2 border-blue-600" />
    <span className="min-w-0 flex-1">
      <b>{c.denominazione}</b>{c.demo && <span className="ml-1 text-xs text-slate-400">demo</span>} <span className="font-mono text-xs text-slate-500">{c.piva}</span>
      <span className="block text-xs text-slate-500">{c.fatture} fatture · {c.anagrafiche} anagrafiche · {c.primanota} registrazioni</span>
      <span className={`mt-1 inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${c.exists ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>{c.exists ? "Presente: aggiungo i mancanti" : "Non presente: la ricreo"}</span>
    </span>
    </label>
    <Button size="sm" variant="outline" className="shrink-0 bg-white" data-testid={`restore-solo-${c.id}`} onClick={solo}>Recupera solo questa</Button>
  </div>
);

export const RestoreBackup = () => {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [list, setList] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/admin/restore/preview", fd); setFile(f); setList(data); setSel([]); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    ref.current.value = "";
  };
  const run = async (only) => {
    const ids = only ? [only] : sel;
    const names = list.filter((c) => ids.includes(c.id)).map((c) => c.denominazione).join(", ");
    if (!window.confirm(`Recuperare i dati mancanti di: ${names}?\nLe altre ditte non vengono toccate e i dati già presenti non vengono modificati.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_ids", ids.join(","));
    setBusy(true);
    try { const { data } = await api.post("/admin/restore", fd); toast.success(`Recupero completato: ${data.totale} record aggiunti`); setList(null); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <input ref={ref} type="file" accept=".zip" className="hidden" data-testid="restore-file-input" onChange={(e) => pick(e.target.files[0])} />
      <Button variant="outline" className="bg-white" data-testid="restore-button" disabled={busy} onClick={() => ref.current.click()}><ArchiveRestore className="mr-2 h-4 w-4" />{busy && !list ? "Lettura\u2026" : "Ripristina da copia"}</Button>
      <Dialog open={!!list} onOpenChange={(o) => !o && setList(null)}>
        <DialogContent data-testid="restore-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Ripristina da copia di sicurezza</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Scegli le ditte da recuperare: spunta la casella a sinistra oppure usa "Recupera solo questa". Vengono aggiunti solo i dati che mancano: niente di ciò che c'è oggi viene modificato o cancellato. Gli utenti non vengono ripristinati.</p>
          <div className="flex gap-3 text-xs">
            <button type="button" data-testid="restore-select-all" className="text-sky-700 underline" onClick={() => setSel((list || []).map((c) => c.id))}>Seleziona tutte</button>
            <button type="button" data-testid="restore-select-none" className="text-slate-500 underline" onClick={() => setSel([])}>Nessuna</button>
          </div>
          <div className="space-y-2">{(list || []).map((c) => <Row key={c.id} c={c} on={sel.includes(c.id)} solo={() => run(c.id)} toggle={(v) => setSel((s) => (v ? [...s, c.id] : s.filter((x) => x !== c.id)))} />)}</div>
          <DialogFooter><Button data-testid="restore-confirm" disabled={!sel.length || busy} onClick={() => run()}>{busy ? "Recupero in corso\u2026" : `Recupera ${sel.length || ""} aziend${sel.length === 1 ? "a" : "e"}`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
